/**
 * Label - Form Label Component
 *
 * Simple wrapper for form field labels that renders as Box.
 * Use with form inputs for accessibility.
 *
 * @example
 * <Label htmlFor="email">Email Address</Label>
 * <Input id="email" type="email" />
 *
 * @example
 * <Label required>Password</Label>
 */
import { forwardRef } from "react";
import { Box } from "@/shared/ui/base";
import { cn } from "@/shared/lib/utils/cn";
import type { BaseComponentProps } from "@/shared/ui/base";

export interface LabelProps extends BaseComponentProps {
  /** Associated input element ID */
  htmlFor?: string;
  /** Show required asterisk */
  required?: boolean;
  /** Disabled state styling */
  disabled?: boolean;
}

/**
 * Label component
 * Renders as a Box with label semantics
 */
export const Label = forwardRef<HTMLLabelElement, LabelProps>(
  ({ htmlFor, required, disabled, className, children, ...props }, ref) => {
    return (
      <Box
        as="label"
        ref={ref}
        htmlFor={htmlFor}
        className={cn(
          "block text-sm font-medium transition-colors",
          disabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700",
          className,
        )}
        {...props}
      >
        {children}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Box>
    );
  },
);

Label.displayName = "Label";
